import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Inbox, Tag, GraduationCap, Calendar, Users, LogOut, Globe, Menu, X } from "lucide-react";
import { useState } from "react";
import { clearToken } from "@/lib/api";

const navItems = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true, testid: "nav-dashboard" },
  { to: "/admin/inbox", icon: Inbox, label: "Anfragen", testid: "nav-inbox" },
  { to: "/admin/angebote", icon: Tag, label: "TM7 Angebote", testid: "nav-angebote" },
  { to: "/admin/einsteiger", icon: GraduationCap, label: "Einsteiger", testid: "nav-einsteiger" },
  { to: "/admin/kochkurse", icon: Calendar, label: "Kochkurse", testid: "nav-kochkurse" },
  { to: "/admin/comeback", icon: Users, label: "Comeback", testid: "nav-comeback" },
];

export default function AdminLayout() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const logout = () => { clearToken(); nav("/admin/login", { replace: true }); };

  const SideContent = (
    <>
      <div className="px-6 pt-7 pb-6 border-b border-white/5">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full overflow-hidden ring-2 ring-emerald-400/40 shadow-lg shadow-emerald-500/20 shrink-0">
            <img
              src="https://customer-assets.emergentagent.com/job_thermomix-zuhause/artifacts/r6wncb9n_cbb0b5ed-96ce-4e2f-9ad2-523d25a84699.jpeg"
              alt="Jennifer Laak"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="min-w-0">
            <div className="text-white font-bold text-sm font-[Outfit] truncate">Jennifer Laak</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-400 font-bold">Admin Panel</div>
          </div>
        </div>
      </div>

      <nav className="flex-1 px-3 py-5 space-y-1">
        {navItems.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.end}
            data-testid={it.testid}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                isActive
                  ? "bg-emerald-500/15 text-emerald-300 border-l-2 border-emerald-400"
                  : "text-slate-400 hover:text-white hover:bg-white/5"
              }`
            }
          >
            <it.icon className="w-4 h-4" />
            {it.label}
          </NavLink>
        ))}
      </nav>

      <div className="p-3 border-t border-white/5 space-y-1" style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}>
        <a href="/" target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-slate-400 hover:text-white hover:bg-white/5 transition">
          <Globe className="w-4 h-4" /> Website ansehen
        </a>
        <button onClick={logout} data-testid="admin-logout"
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold bg-red-500/10 text-red-300 hover:bg-red-500/20 hover:text-red-200 border border-red-500/20 transition">
          <LogOut className="w-4 h-4" /> Abmelden
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-slate-950 text-slate-100 overflow-x-hidden">
      {/* Mobile topbar – nur wenn Sidebar zu */}
      {!open && (
        <header className="lg:hidden fixed top-0 left-0 right-0 z-40 flex items-center justify-between px-4 h-14 bg-slate-900/90 backdrop-blur-md border-b border-white/5">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full overflow-hidden ring-2 ring-emerald-400/40">
              <img
                src="https://customer-assets.emergentagent.com/job_thermomix-zuhause/artifacts/r6wncb9n_cbb0b5ed-96ce-4e2f-9ad2-523d25a84699.jpeg"
                alt="Jennifer Laak"
                className="w-full h-full object-cover"
              />
            </div>
            <span className="text-sm font-bold text-white">Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={logout} data-testid="admin-logout-mobile"
              title="Abmelden"
              className="w-9 h-9 rounded-lg bg-red-500/10 hover:bg-red-500/20 flex items-center justify-center text-red-300 border border-red-500/20 transition">
              <LogOut className="w-4 h-4" />
            </button>
            <button onClick={() => setOpen(true)} data-testid="admin-menu-toggle"
              className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center text-white">
              <Menu className="w-4 h-4" />
            </button>
          </div>
        </header>
      )}

      {/* Sidebar – mobile: Vollscreen-Drawer mit höherem z-Index */}
      <aside className={`${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 fixed lg:sticky top-0 left-0 z-50 lg:z-30 w-72 lg:w-64 h-screen flex flex-col bg-slate-900 border-r border-white/5 transition-transform shadow-2xl lg:shadow-none`}>
        {open && (
          <button onClick={() => setOpen(false)} data-testid="admin-menu-close"
            className="lg:hidden absolute top-4 right-4 w-9 h-9 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center text-white z-10">
            <X className="w-4 h-4" />
          </button>
        )}
        {SideContent}
      </aside>

      {open && <div className="lg:hidden fixed inset-0 bg-black/70 z-40" onClick={() => setOpen(false)} />}

      <main className="flex-1 min-w-0 pt-14 lg:pt-0">
        <div className="p-4 sm:p-5 md:p-8 max-w-7xl mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
